import { createAction } from '@reduxjs/toolkit'
import { all, call, put, select, throttle } from 'redux-saga/effects'

import { isActiveDrawableHeader } from '../utils/isActiveDrawableHeader'
import {
    generateUpdateDrawableCoordinatesEndpointData,
    selectUpdateCoordinatesState,
    UpdateCoordinatesState,
} from './updateDrawableCoordinates'
import { UPDATE_OPENING_GROUPS_SUCCESS, updatedDrawableMeasurements } from '../../../actions/drawable'
import { UpdateOpeningGroupApiResponse } from '../../../api/api-helper'
import { bulkUpdateOpeningLocationData } from '../../../api/takeoff-api'
import { Coordinate } from '../../../models/activeDrawable'
import { ActiveFloor } from '../../../models/activeFloor'
import { DRAWABLE_TYPES } from '../../../shared/constants/drawable-types'
import { isHeaderAndBeamGroup } from '../../../shared/services/drawable-groups-service'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Label, Select, Workspace } from '../../lib/toolBoxes/2D'
import { getCurrentDrawableLocationsFromRootState, updateDrawableLocationCoordinates } from '../../slices/2D'
import { updateToolbarMessage } from '../../slices/common'
import { selectDrawableActiveFloor } from '../../slices/documents'
import { setFormPendingStatus } from '../../slices/forms'
import { Geometry, selectGeometries, updateGeometricLocationAdditionalData } from '../../slices/geometry'
import {
    IMUP2DCoordinatesToUpdate,
    IMUP2DDrawableCutout,
    IMUP2DDrawableLocation,
    MeasurementsToUpdate,
    SAGA_THROTTLE_TIMER,
    SAVING_MESSAGE,
} from '../../types'

export type BulkUpdateOpeningLocationCoordinatesPayload = (IMUP2DCoordinatesToUpdate & {
    opening_id: number
    opening_group_id: number
    opening_location_id: number
})[]

export const bulkUpdateOpeningLocationCoordinatesAction = createAction<BulkUpdateOpeningLocationCoordinatesPayload>(
    'bulkUpdateOpeningLocationCoordinates'
)

export function syncCanvasItemsWithDestinationGroups(
    items: paper.Item[],
    payload: BulkUpdateOpeningLocationCoordinatesPayload,
    response: UpdateOpeningGroupApiResponse[]
) {
    const movedLocationIds = new Set(payload.map(({ opening_location_id }) => opening_location_id))
    const destinationByLocationId = new Map<number, { openingGroupId: number; openingId: number }>()

    response.forEach(({ newGroup }) => {
        newGroup.openings.forEach(({ id: openingId, opening_locations }) => {
            opening_locations.forEach(({ id }) => {
                if (movedLocationIds.has(id)) {
                    destinationByLocationId.set(id, {
                        openingGroupId: newGroup.id,
                        openingId,
                    })
                }
            })
        })
    })

    items.forEach((item) => {
        const destination = destinationByLocationId.get(item.data.opening_location_id)

        if (destination) {
            item.data.opening_group_id = destination.openingGroupId
            item.data.drawable_id = destination.openingId
        }
    })
}

export function* handleBulkUpdateOpeningLocationCoordinates({
    payload,
}: ReturnType<typeof bulkUpdateOpeningLocationCoordinatesAction>) {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (!manager) return

    yield put(updateToolbarMessage(SAVING_MESSAGE))

    const [workspace, selectTool, label]: [Workspace, Select, Label] = yield call(manager.getTools, [
        Workspace.NAME,
        Select.NAME,
        Label.NAME,
    ])
    const geometries: Geometry[] = yield select(selectGeometries)

    const locations: IMUP2DDrawableLocation[] = yield select(getCurrentDrawableLocationsFromRootState)
    const activeFloor: ActiveFloor = yield select(selectDrawableActiveFloor)
    const items: paper.Item[] = yield call(workspace.getAllDrawableItems)

    const coordinatesDataResults: {
        coordinatesData: IMUP2DCoordinatesToUpdate
        projectId: number
        regionId: number | null
    }[] = yield all(
        payload.map((actionInput) => {
            const coordinates: IMUP2DCoordinatesToUpdate = {
                coordinates: actionInput.coordinates,
                cutouts: actionInput.cutouts,
                measurements: actionInput.measurements,
            }
            const geo = geometries.find((geo) => geo.id === actionInput.opening_location_id)

            if (geo) {
                if (isHeaderAndBeamGroup(geo.type, geo.settings)) {
                    const potentialPathItems = selectTool.getMultiSelectedItems()

                    const pathItem = potentialPathItems?.find(
                        (potentialPathItem) =>
                            potentialPathItem.className === 'Path' &&
                            potentialPathItem.data.drawable_id === actionInput.opening_id
                    )

                    if (pathItem) {
                        const pathParent = selectTool.getMultiSelectedItemParent(pathItem.id)

                        if (pathParent && pathParent.parent !== null) {
                            label.updateLabelPosition(pathParent as paper.Group, pathItem as paper.Path)
                        }
                    }
                }
            }

            return call(
                generateUpdateDrawableCoordinatesEndpointData,
                coordinates,
                items.filter(
                    (item) => item.data.opening_location_id === actionInput.opening_location_id
                )?.[0] as paper.PathItem,
                locations.filter((loc) => loc.opening_location_id === actionInput.opening_location_id)?.[0],
                activeFloor,
                workspace,
                { settings: geo?.settings, type: geo?.type }
            )
        })
    )

    const regionIdByLocationId = new Map<number, number | null>()

    payload.forEach((p, i) => {
        regionIdByLocationId.set(p.opening_location_id, coordinatesDataResults[i]?.regionId ?? null)
    })

    const apiInputData: {
        opening_id: number
        opening_location_id: number
        coordinates: Coordinate[]
        measurements: MeasurementsToUpdate
        cutouts?: IMUP2DDrawableCutout[]
        region_id?: number | null
    }[] = yield coordinatesDataResults.map((data, i) => {
        return {
            opening_id: payload[i].opening_id,
            opening_location_id: payload[i].opening_location_id,
            coordinates: data.coordinatesData.coordinates,
            measurements: data.coordinatesData.measurements,
            cutouts: data.coordinatesData.cutouts,
            region_id: data.regionId,
        }
    })

    try {
        const { activeDrawableGroup }: UpdateCoordinatesState = yield select(selectUpdateCoordinatesState)

        if (activeDrawableGroup && isActiveDrawableHeader(activeDrawableGroup)) {
            yield put(setFormPendingStatus(true))
        }

        const response: UpdateOpeningGroupApiResponse[] = yield call(
            bulkUpdateOpeningLocationData,
            coordinatesDataResults[0].projectId,
            apiInputData
        )

        yield call(syncCanvasItemsWithDestinationGroups, items, payload, response)

        const openingLocations = payload.flatMap((o) => o.opening_location_id)

        const pendingGeometricLocationUpdates = response.flatMap(({ newGroup: { openings } }) =>
            openings.flatMap(({ additional_data: openingAdditionalData, opening_locations }) =>
                opening_locations
                    .filter((loc) => openingLocations.includes(loc.id))
                    .map((loc) => {
                        return put(
                            updateGeometricLocationAdditionalData({
                                id: loc.id,
                                locationAdditionalData: loc.additional_data,
                                openingAdditionalData,
                            })
                        )
                    })
            )
        )

        if (pendingGeometricLocationUpdates.length) {
            yield all(pendingGeometricLocationUpdates)
        }

        // Merge openings from all API responses per destination group
        const destinationGroupMap = new Map<
            number,
            { group: UpdateOpeningGroupApiResponse['newGroup']; mergedOpeningIds: Set<number> }
        >()

        response.forEach((newAndOldGroup) => {
            const newGroupId = newAndOldGroup.newGroup.id
            const existingEntry = destinationGroupMap.get(newGroupId)

            if (!existingEntry) {
                destinationGroupMap.set(newGroupId, {
                    group: { ...newAndOldGroup.newGroup },
                    mergedOpeningIds: new Set(newAndOldGroup.newGroup.openings?.map((o) => o.id) ?? []),
                })
            } else {
                const newOpenings = newAndOldGroup.newGroup.openings ?? []

                newOpenings.forEach((opening) => {
                    if (!existingEntry.mergedOpeningIds.has(opening.id)) {
                        existingEntry.group.openings = [...(existingEntry.group.openings ?? []), opening]
                        existingEntry.mergedOpeningIds.add(opening.id)
                    } else {
                        existingEntry.group.openings = existingEntry.group.openings?.map((o) =>
                            o.id === opening.id ? opening : o
                        )
                    }
                })
                existingEntry.group.settings = newAndOldGroup.newGroup.settings
            }
        })

        // Build group updates with synthetic originalGroup to filter out old geometries
        const groupUpdates: {
            type: string
            payload: {
                openingGroups: UpdateOpeningGroupApiResponse
                skipCanvasRedraw: boolean
            }
        }[] = []

        destinationGroupMap.forEach(({ group }) => {
            if (group.type === DRAWABLE_TYPES.HIGHLIGHTER) {
                return
            }

            // Only include synthetics for openings that belong to this group
            const groupOpeningSynthetics = payload
                .filter((p) => p.opening_group_id === group.id)
                .map((p) => ({
                    id: p.opening_id,
                    opening_locations: [{ id: p.opening_location_id }],
                })) as UpdateOpeningGroupApiResponse['newGroup']['openings']

            const syntheticOriginalGroup = {
                ...group,
                openings: [...(group.openings || []), ...groupOpeningSynthetics],
            }

            groupUpdates.push({
                type: UPDATE_OPENING_GROUPS_SUCCESS,
                payload: {
                    openingGroups: {
                        newGroup: group,
                        originalGroup: syntheticOriginalGroup,
                    },
                    skipCanvasRedraw: true,
                },
            })
        })

        yield put(setFormPendingStatus(false))

        // Update coordinates BEFORE UPDATE_OPENING_GROUPS_SUCCESS (saga reads from Redux state)
        yield all(
            payload.flatMap((payloadItem) => {
                const openingId = payloadItem.opening_id
                const openingLocationId = payloadItem.opening_location_id
                const openingGroupId = payloadItem.opening_group_id

                // Find the response that contains this opening's group
                const responseForOpening = response.find(
                    (r) => r.newGroup.id === openingGroupId || r.newGroup.openings.some((o) => o.id === openingId)
                )

                const regionId = regionIdByLocationId.get(openingLocationId) ?? null

                if (!responseForOpening) {
                    // Fallback: just update coordinates without group measurements
                    return [
                        put(
                            updateDrawableLocationCoordinates({
                                opening_location_id: openingLocationId,
                                coordinates: payloadItem.coordinates,
                                linear_total: null,
                                quantity: null,
                                cutouts: payloadItem.cutouts,
                                region_id: regionId,
                            })
                        ),
                        put(
                            updatedDrawableMeasurements({
                                activeDrawableId: openingId,
                                groupMeasurements: { quantity: null, linear_total: null },
                                openingMeasurements: { area: null, quantity: null, linear_total: null },
                                cutouts: payloadItem.cutouts,
                                coordinates: payloadItem.coordinates,
                                updatedLocationID: openingLocationId,
                            })
                        ),
                    ]
                }

                const newGroup = responseForOpening.newGroup
                const newDrawable = newGroup.openings.find((drawable) => drawable.id === openingId)

                const newQuantity = newDrawable?.settings.quantity
                const newLinearTotal = newDrawable?.settings.linear_total
                const newCutouts = payloadItem.cutouts

                // update the quantity & linear total inside the activeDrawableGroup and drawable groups
                const groupMeasurements = {
                    quantity: newGroup.settings.quantity,
                    linear_total: newGroup.settings.linear_total,
                }

                const opening = newGroup.openings.find((o) => o.id === openingId)
                const openingMeasurements = {
                    area: opening?.settings.area,
                    quantity: opening?.settings.quantity ?? null,
                    linear_total: opening?.settings.linear_total ?? null,
                }

                return [
                    put(
                        updateDrawableLocationCoordinates({
                            opening_location_id: openingLocationId,
                            coordinates: payloadItem.coordinates,
                            linear_total: newLinearTotal ?? null,
                            quantity: newQuantity ?? null,
                            cutouts: newCutouts,
                            region_id: regionId,
                        })
                    ),
                    put(
                        updatedDrawableMeasurements({
                            activeDrawableId: openingId,
                            groupMeasurements,
                            openingMeasurements,
                            cutouts: newCutouts,
                            coordinates: payloadItem.coordinates,
                            updatedLocationID: openingLocationId,
                        })
                    ),
                ]
            })
        )

        // NOW dispatch UPDATE_OPENING_GROUPS_SUCCESS after coordinates are updated in Redux
        // This ensures handleUpdateOpeningGroupsSuccess2D reads the correct (new) coordinates
        for (const update of groupUpdates) {
            yield put(update)
        }
    } catch (e) {
        yield call(console.error, e)
    } finally {
        yield put(updateToolbarMessage(null))
    }
}

export function* watchForBuildBulkUpdateOpeningLocationCoordinates() {
    yield throttle(
        SAGA_THROTTLE_TIMER,
        bulkUpdateOpeningLocationCoordinatesAction.type,
        handleBulkUpdateOpeningLocationCoordinates
    )
}
